import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import { format, formatDistanceToNow, differenceInDays, isThisYear, isFuture, isPast } from 'date-fns';
import { LeaveRequestRecord, LeaveRequestStatus } from '../types';

const statusColorMap: Record<LeaveRequestStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  DENIED: 'error',
  CANCELLED: 'default',
  CHANGE_PENDING: 'info',
};

const statusLabelMap: Record<LeaveRequestStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  CANCELLED: 'Cancelled',
  CHANGE_PENDING: 'Change requested',
};

interface LeaveRequestListProps {
  title?: string;
  requests: LeaveRequestRecord[];
  loading?: boolean;
  showStaffColumn?: boolean;
  emptyState?: React.ReactNode;
  actionsSlot?: React.ReactNode;
  renderActions?: (request: LeaveRequestRecord) => React.ReactNode;
}

const formatTimestamp = (value?: any): string => {
  if (!value || typeof value.toDate !== 'function') return '-';
  return format(value.toDate(), 'dd MMM yyyy');
};

const formatDateRange = (start?: any, end?: any): string => {
  if (!start || !end || typeof start.toDate !== 'function' || typeof end.toDate !== 'function') {
    return '-';
  }
  return `${format(start.toDate(), 'dd MMM yyyy')} → ${format(end.toDate(), 'dd MMM yyyy')}`;
};

interface EnhancedDateRangeProps {
  startDate: any;
  endDate: any;
  totalDays: number;
}

const EnhancedDateRange: React.FC<EnhancedDateRangeProps> = ({ startDate, endDate, totalDays }) => {
  if (!startDate || !endDate || typeof startDate.toDate !== 'function' || typeof endDate.toDate !== 'function') {
    return <Typography variant="body2">-</Typography>;
  }

  const start = startDate.toDate();
  const end = endDate.toDate();
  const today = new Date();

  const isUpcoming = isFuture(start);
  const isOngoing = isPast(start) && isFuture(end);
  const isCompleted = isPast(end);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();
  const currentYear = isThisYear(start) && isThisYear(end);

  // Determine status color and icon
  const getStatusIndicator = () => {
    if (isOngoing) {
      return { color: 'success.main', icon: <TodayIcon fontSize="small" />, label: 'Ongoing' };
    }
    if (isUpcoming) {
      const daysUntil = differenceInDays(start, today);
      if (daysUntil <= 7) {
        return { color: 'warning.main', icon: <EventIcon fontSize="small" />, label: `In ${daysUntil}d` };
      }
      return { color: 'info.main', icon: <CalendarTodayIcon fontSize="small" />, label: 'Upcoming' };
    }
    if (isCompleted) {
      return { color: 'text.secondary', icon: <CalendarTodayIcon fontSize="small" />, label: 'Past' };
    }
    return { color: 'text.secondary', icon: <CalendarTodayIcon fontSize="small" />, label: '' };
  };

  const statusIndicator = getStatusIndicator();

  // Format date range intelligently
  const formatSmartRange = () => {
    if (sameMonth) {
      // Same month: "5-12 Jan 2025" or "5-12 Jan" if current year
      const dayRange = `${format(start, 'd')}-${format(end, 'd')}`;
      return currentYear
        ? `${dayRange} ${format(start, 'MMM')}`
        : `${dayRange} ${format(start, 'MMM yyyy')}`;
    }

    if (sameYear && currentYear) {
      // Same year, current year: "28 Dec - 3 Jan"
      return `${format(start, 'd MMM')} - ${format(end, 'd MMM')}`;
    }

    if (sameYear && !currentYear) {
      // Same year, not current: "28 Dec - 3 Jan 2024"
      return `${format(start, 'd MMM')} - ${format(end, 'd MMM yyyy')}`;
    }

    // Different years: "28 Dec 2024 - 3 Jan 2025"
    return `${format(start, 'd MMM yy')} - ${format(end, 'd MMM yy')}`;
  };

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" display="block">
            Start: {format(start, 'EEEE, dd MMMM yyyy')}
          </Typography>
          <Typography variant="caption" display="block">
            End: {format(end, 'EEEE, dd MMMM yyyy')}
          </Typography>
          <Typography variant="caption" display="block" fontWeight={600} sx={{ mt: 0.5 }}>
            {totalDays} day{totalDays !== 1 ? 's' : ''} total
          </Typography>
          {isUpcoming && (
            <Typography variant="caption" display="block" color="info.light">
              Starts {formatDistanceToNow(start, { addSuffix: true })}
            </Typography>
          )}
          {isOngoing && (
            <Typography variant="caption" display="block" color="success.light">
              Ends {formatDistanceToNow(end, { addSuffix: true })}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: statusIndicator.color, display: 'flex', alignItems: 'center' }}>
          {statusIndicator.icon}
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {formatSmartRange()}
          </Typography>
          {statusIndicator.label && (
            <Typography variant="caption" sx={{ color: statusIndicator.color, lineHeight: 1 }}>
              {statusIndicator.label}
            </Typography>
          )}
        </Box>
      </Stack>
    </Tooltip>
  );
};

const LeaveRequestRow: React.FC<{
  request: LeaveRequestRecord;
  showStaffColumn: boolean;
  renderActions?: (request: LeaveRequestRecord) => React.ReactNode;
}> = ({ request, showStaffColumn, renderActions }) => {
  const [open, setOpen] = useState(false);

  const hasExpandableContent =
    (request.reason && request.reason.length > 50) ||
    (request.comments?.staff && request.comments.staff.length > 50) ||
    (request.comments?.manager && request.comments.manager.length > 50) ||
    request.history?.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: open ? 'none' : undefined },
          cursor: hasExpandableContent ? 'pointer' : 'default',
          transition: 'background-color 0.2s ease',
        }}
        onClick={() => hasExpandableContent && setOpen(!open)}
      >
        <TableCell sx={{ width: 48 }}>
          {hasExpandableContent && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        {showStaffColumn && (
          <TableCell sx={{ minWidth: 160 }}>
            <Typography variant="body2" fontWeight={600}>
              {request.staff.displayName}
            </Typography>
            {request.staff.coachId && (
              <Typography variant="caption" color="text.secondary">
                #{request.staff.coachId}
              </Typography>
            )}
          </TableCell>
        )}
        <TableCell sx={{ minWidth: 200 }}>
          <EnhancedDateRange
            startDate={request.period.startDate}
            endDate={request.period.endDate}
            totalDays={request.period.totalDays}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`${request.period.totalDays} day${request.period.totalDays !== 1 ? 's' : ''}`}
            variant="outlined"
            sx={{ minWidth: 60, fontWeight: 600 }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={statusLabelMap[request.status] || request.status}
            color={statusColorMap[request.status] || 'default'}
            sx={{ fontWeight: 600 }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(request.timeline?.updatedAt || request.timeline?.createdAt)}
          </Typography>
        </TableCell>
        {renderActions && (
          <TableCell align="right" sx={{ minWidth: 140 }} onClick={(e) => e.stopPropagation()}>
            {renderActions(request)}
          </TableCell>
        )}
      </TableRow>
      {hasExpandableContent && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={showStaffColumn ? 7 : 6}>
            <Collapse in={open} timeout={300} unmountOnExit>
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  my: 1,
                  mx: 1,
                  backgroundColor: 'action.hover',
                  borderRadius: 2,
                  borderLeft: '4px solid',
                  borderColor:
                    request.status === 'APPROVED' ? 'success.main'
                    : request.status === 'PENDING' ? 'warning.main'
                    : request.status === 'DENIED' ? 'error.main'
                    : 'info.main',
                }}
              >
                {/* Main Content Grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: request.history && request.history.length > 0
                        ? 'minmax(200px, 1fr) minmax(200px, 1fr) minmax(200px, 1fr)'
                        : (request.comments?.staff || request.comments?.manager)
                        ? 'minmax(200px, 1fr) minmax(200px, 1fr)'
                        : '1fr'
                    },
                    gap: 3,
                  }}
                >
                  {/* Reason Section */}
                  {request.reason && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
                          Reason
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {request.reason}
                      </Typography>
                    </Box>
                  )}

                  {/* Comments Section */}
                  {(request.comments?.staff || request.comments?.manager) && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CommentOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
                          Comments
                        </Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        {request.comments?.staff && (
                          <Box>
                            <Typography variant="caption" fontWeight={600} color="info.main" display="block" sx={{ mb: 0.5 }}>
                              Staff:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                              {request.comments.staff}
                            </Typography>
                          </Box>
                        )}
                        {request.comments?.manager && (
                          <Box>
                            <Typography variant="caption" fontWeight={600} color="primary.main" display="block" sx={{ mb: 0.5 }}>
                              Manager:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                              {request.comments.manager}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  )}

                  {/* History Timeline */}
                  {request.history && request.history.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <TimelineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="overline" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
                          Timeline
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        {request.history.map((entry, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor:
                                  entry.status === 'APPROVED' ? 'success.main'
                                  : entry.status === 'PENDING' ? 'warning.main'
                                  : entry.status === 'DENIED' ? 'error.main'
                                  : 'info.main',
                                flexShrink: 0,
                                mt: 0.5,
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                                <Chip
                                  size="small"
                                  label={statusLabelMap[entry.status] || entry.status}
                                  color={statusColorMap[entry.status] || 'default'}
                                  sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {entry.changedBy?.displayName || 'System'}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  •
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                  {formatTimestamp(entry.changedAt)}
                                </Typography>
                              </Box>
                              {entry.note && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                                  "{entry.note}"
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const LeaveRequestList: React.FC<LeaveRequestListProps> = ({
  title,
  requests,
  loading = false,
  showStaffColumn = false,
  emptyState,
  actionsSlot,
  renderActions,
}) => {
  const hasRequests = requests.length > 0;

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      {(title || actionsSlot) && (
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {title && (
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
          )}
          {actionsSlot}
        </Box>
      )}

      {loading && <LinearProgress />}

      {!loading && !hasRequests && (
        <Box sx={{ px: 3, py: 4 }}>
          {emptyState || (
            <Typography variant="body2" color="text.secondary">
              No leave requests match the current filters yet.
            </Typography>
          )}
        </Box>
      )}

      {hasRequests && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                {showStaffColumn && <TableCell>Staff</TableCell>}
                <TableCell>Leave Period</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                {renderActions && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <LeaveRequestRow
                  key={request.id}
                  request={request}
                  showStaffColumn={showStaffColumn}
                  renderActions={renderActions}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default LeaveRequestList;
